/**
 * @file src/helpers/ory/errors.tsx
 * @exports AxiosErrorType, handleFlowError
 * @summary Handles Ory browser-flow errors and redirects recoverable flows.
 */

import { showToast } from "@cerebruminc/cerebellum";
import type { AxiosError } from "axios";
import queryString from "query-string";
import type { Dispatch, SetStateAction } from "react";

import { paths } from "src/const";
import { navigateApp } from "src/lib/app-router";
import { createRedirectionWithParams, validateReturnToUrl } from "./helpers";

export interface AxiosErrorType {
	error: { id?: string; code?: number };
	redirect_browser_to: string;
}

// A small function to help us deal with errors coming from fetching a flow.
export function handleFlowError<S>(flowType: string, resetFlow: Dispatch<SetStateAction<S | undefined>>) {
	return (err: AxiosError<AxiosErrorType>) => {
		// getting return_to
		const { query: params } = queryString.parseUrl(window.location.href);
		const returnTo = validateReturnToUrl(params?.return_to as string) ? (params?.return_to as string) : undefined;

		if (err.response?.data?.error?.code === 401) {
			navigateApp({ href: paths.login });
			return;
		}

		switch (err.response?.data?.error?.id) {
			case "session_aal2_required": {
				// 2FA is enabled and enforced, but user did not perform 2fa yet!
				window.location.replace(err.response.data?.redirect_browser_to);
				return;
			}
			case "session_already_available":
				// User is already signed in, let's redirect them to returnTo param or home!
				if (returnTo) {
					const finalUrl = createRedirectionWithParams(returnTo);
					window.location.href = finalUrl as string;
				} else {
					navigateApp({ href: paths.home });
				}
				return;
			case "session_refresh_required":
				// We need to re-authenticate to perform this action
				window.location.href = err.response?.data?.redirect_browser_to;
				return;
			case "self_service_flow_return_to_forbidden":
				// The flow expired, let's request a new one.
				showToast({
					message: "The return_to address is not allowed.",
					tone: "negative",
				});
				resetFlow(undefined);
				window.location.href = flowType;
				return;
			case "self_service_flow_expired":
				// The flow expired, let's request a new one.
				showToast({
					message: "Your interaction expired, please fill out the form again.",
					tone: "negative",
				});
				resetFlow(undefined);
				navigateApp({ href: flowType });
				return;
			case "security_csrf_violation":
				// A CSRF violation occurred. Best to just refresh the flow!
				showToast({
					message: "A security violation was detected, please fill out the form again.",
					tone: "negative",
				});
				resetFlow(undefined);
				navigateApp({ href: flowType });
				return;
			case "security_identity_mismatch":
				// The requested item was intended for someone else. Let's request a new flow...
				resetFlow(undefined);
				navigateApp({ href: flowType });
				return;
			case "browser_location_change_required": {
				// Ory Kratos asked us to point the user to this URL (e.g. OIDC redirect).
				const url = err.response.data?.redirect_browser_to;
				if (url) {
					window.location.replace(url);
				} else {
					showToast({
						message: "We were unable to redirect you. Please try again.",
						tone: "negative",
					});
					resetFlow(undefined);
					navigateApp({ href: flowType });
				}
				return;
			}
			case "session_inactive":
				navigateApp({ href: paths.login });
				return;
		}

		switch (err.response?.status) {
			case 410:
				// The flow expired, let's request a new one.
				resetFlow(undefined);
				navigateApp({ href: flowType });
				return;
		}

		// We are not able to handle the error? Return it.
		return err;
	};
}
